// Domains — view, create, edit, delete domains. Edit their field schema.

import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import type { Domain } from "@/lib/types";
import { Button } from "@/components/primitives/Button";
import { IconButton } from "@/components/primitives/IconButton";
import { TextField } from "@/components/primitives/TextField";
import { Dialog } from "@/components/primitives/Dialog";
import { Drawer } from "@/components/primitives/Drawer";
import { Chip } from "@/components/primitives/Chip";
import { EmptyState } from "@/components/primitives/EmptyState";
import { useToast } from "@/components/primitives/Toast";
import { Icon, type IconName } from "@/lib/icons";

const FIELD_TYPES = ["string", "number", "date", "boolean", "enum", "ref", "code", "json"] as const;
type FieldType = typeof FIELD_TYPES[number];

interface FieldRow {
  key: string;
  label: string;
  type: FieldType;
  required: boolean;
  searchable: boolean;
  enumValues: string; // comma-separated
  refDomain: string;
  description: string;
}

function emptyField(sortOrder: number): FieldRow {
  return {
    key: `field_${sortOrder + 1}`,
    label: `Field ${sortOrder + 1}`,
    type: "string",
    required: false,
    searchable: false,
    enumValues: "",
    refDomain: "",
    description: "",
  };
}

const DOMAIN_ICONS: IconName[] = ["Database", "Folder", "Globe", "User", "Tag", "Quality", "Map", "Tree", "Upload"];

const css: Record<string, CSSProperties> = {
  page: { display: "flex", flexDirection: "column", gap: 16, padding: 24, maxWidth: 1200, margin: "0 auto" },
  head: { display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap" },
  toolbar: { display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" },
  list: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16, listStyle: "none", margin: 0, padding: 0 },
  card: { background: "var(--md-surface)", border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-lg)", padding: 16, display: "flex", flexDirection: "column", gap: 8 },
  cardHead: { display: "flex", alignItems: "center", gap: 10 },
  iconBubble: { width: 36, height: 36, borderRadius: "var(--shape-squircle)", background: "var(--md-primary-container)", color: "var(--md-on-primary-container)", display: "grid", placeItems: "center" },
  meta: { display: "flex", gap: 10, color: "var(--md-on-surface-var)", font: "var(--t-body-sm)", flexWrap: "wrap" },
  actions: { display: "flex", gap: 6, marginTop: "auto", paddingTop: 8 },
  drawerForm: { display: "flex", flexDirection: "column", gap: 14 },
  fieldRow: { display: "grid", gridTemplateColumns: "1fr 1fr 110px auto", gap: 8, alignItems: "center", padding: 10, border: "1px solid var(--md-outline-var)", borderRadius: "var(--r-md)" },
  fieldRowDetail: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 },
  fieldCheckboxes: { display: "flex", gap: 14, fontSize: 12, color: "var(--md-on-surface-var)" },
  iconPicker: { display: "flex", gap: 6, flexWrap: "wrap" },
  iconChoice: { width: 32, height: 32, borderRadius: "var(--r-sm)", border: "1px solid var(--md-outline-var)", background: "var(--md-surface-bright)", display: "grid", placeItems: "center", cursor: "pointer" },
  iconChoiceActive: { borderColor: "var(--md-primary)", background: "var(--md-primary-container)", color: "var(--md-on-primary-container)" },
};

export function Domains() {
  const navigate = useNavigate();
  const toast = useToast();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const refresh = async () => {
    try {
      const list = await api.domains.list();
      setDomains(list);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Failed to load domains", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const onMutation = () => refresh();
    window.addEventListener("aidan:mutation", onMutation);
    return () => window.removeEventListener("aidan:mutation", onMutation);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return domains;
    const lc = q.toLowerCase();
    return domains.filter(d =>
      d.id.toLowerCase().includes(lc) ||
      d.name.toLowerCase().includes(lc) ||
      (d.description ?? "").toLowerCase().includes(lc) ||
      (d.owner ?? "").toLowerCase().includes(lc),
    );
  }, [domains, q]);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newOwner, setNewOwner] = useState("");
  const [newIcon, setNewIcon] = useState<IconName>("Database");
  const [creating, setCreating] = useState(false);
  const onCreate = async () => {
    const name = newName.trim();
    if (!name) { toast("Name is required", "warn"); return; }
    setCreating(true);
    try {
      const created = await api.domains.create({
        name,
        description: newDesc.trim() || undefined,
        owner: newOwner.trim() || undefined,
        icon: newIcon,
      });
      toast(`Created “${created.name}”`, "ok");
      setCreateOpen(false);
      setNewName(""); setNewDesc(""); setNewOwner(""); setNewIcon("Database");
      await refresh();
      setEditing(created); // open edit drawer immediately so user can add fields
    } catch (e) {
      toast(e instanceof Error ? e.message : "Create failed", "error");
    } finally {
      setCreating(false);
    }
  };

  // Edit drawer
  const [editing, setEditing] = useState<Domain | null>(null);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editOwner, setEditOwner] = useState("");
  const [editIcon, setEditIcon] = useState<IconName>("Database");
  const [fields, setFields] = useState<FieldRow[]>([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    setEditName(editing.name);
    setEditDesc(editing.description ?? "");
    setEditOwner(editing.owner ?? "");
    setEditIcon((editing.icon as IconName) ?? "Database");
    setFieldsLoading(true);
    api.domains.schema(editing.id)
      .then(s => setFields((s.fields ?? []).map((f) => ({
        key: f.key,
        label: f.label ?? f.key,
        type: (FIELD_TYPES.includes(f.type as FieldType) ? f.type : "string") as FieldType,
        required: Boolean((f as { required?: boolean }).required),
        searchable: Boolean((f as { searchable?: boolean }).searchable),
        enumValues: ((f as { enum?: string[] }).enum ?? []).join(", "),
        refDomain: (f as { refDomain?: string }).refDomain ?? "",
        description: (f as { description?: string }).description ?? "",
      }))))
      .catch(() => setFields([]))
      .finally(() => setFieldsLoading(false));
  }, [editing]);

  const closeEdit = () => { setEditing(null); setFields([]); };

  const addField = () => setFields(f => [...f, emptyField(f.length)]);
  const removeField = (idx: number) => setFields(f => f.filter((_, i) => i !== idx));
  const moveField = (idx: number, delta: number) => setFields(f => {
    const next = idx + delta;
    if (next < 0 || next >= f.length) return f;
    const copy = [...f];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    return copy;
  });
  const updateField = (idx: number, patch: Partial<FieldRow>) =>
    setFields(f => f.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

  const saveEdits = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const metaPatch: Partial<{ name: string; description: string; icon: string; owner: string }> = {};
      if (editName.trim() && editName.trim() !== editing.name) metaPatch.name = editName.trim();
      if ((editDesc || "") !== (editing.description ?? "")) metaPatch.description = editDesc;
      if ((editOwner || "") !== (editing.owner ?? "")) metaPatch.owner = editOwner;
      if (editIcon !== (editing.icon ?? "Database")) metaPatch.icon = editIcon;
      if (Object.keys(metaPatch).length) {
        await api.domains.update(editing.id, metaPatch);
      }
      const seen = new Set<string>();
      for (const f of fields) {
        if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(f.key)) {
          toast(`Invalid field key “${f.key}” — use letters, digits, underscore.`, "error");
          setSaving(false);
          return;
        }
        if (seen.has(f.key)) {
          toast(`Duplicate field key “${f.key}”`, "error");
          setSaving(false);
          return;
        }
        seen.add(f.key);
      }
      await api.domains.setSchema(editing.id, fields.map((f, i) => ({
        key: f.key,
        label: f.label || f.key,
        type: f.type,
        required: f.required,
        searchable: f.searchable,
        enum: f.type === "enum"
          ? f.enumValues.split(",").map(s => s.trim()).filter(Boolean)
          : undefined,
        refDomain: f.type === "ref" && f.refDomain ? f.refDomain : undefined,
        description: f.description || undefined,
        sortOrder: i,
      })));
      toast("Saved", "ok");
      await refresh();
      closeEdit();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (d: Domain) => {
    if (d.recordCount > 0) {
      toast(`Cannot delete “${d.name}” — has ${d.recordCount} records`, "warn");
      return;
    }
    if (!confirm(`Delete domain “${d.name}”? This cannot be undone.`)) return;
    try {
      await api.domains.remove(d.id);
      toast(`Deleted “${d.name}”`, "ok");
      await refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : "Delete failed", "error");
    }
  };

  return (
    <div className="lex-page" style={css.page}>
      <header style={css.head}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <span className="lex-eyebrow">REFERENCE DATA</span>
          <h1 className="lex-display" style={{ fontSize: "clamp(24px, 3vw, 34px)", margin: 0 }}>
            Domains <span className="lex-display__em">& schema.</span>
          </h1>
          <p style={{ color: "var(--md-on-surface-var)", margin: "8px 0 0", maxWidth: 640 }}>
            Every reference dataset is a domain. Define the fields each record carries, who owns it,
            and what the audience sees.
          </p>
        </div>
        <div style={css.toolbar}>
          <TextField
            label="Search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search domains…"
            leadingIcon={<Icon.Search size={16} />}
            size="sm"
          />
          <Button
            variant="filled"
            tone="primary"
            leadingIcon={<Icon.Plus size={16} />}
            onClick={() => setCreateOpen(true)}
          >
            New domain
          </Button>
        </div>
      </header>

      {loading ? (
        <p style={{ color: "var(--md-on-surface-var)" }}>Loading domains…</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={q ? `No domains match "${q}"` : "No domains yet"}
          message={q ? "Try a different search." : "Create your first domain to start managing reference data."}
          action={q ? undefined : <Button onClick={() => setCreateOpen(true)} leadingIcon={<Icon.Plus size={16} />}>New domain</Button>}
        />
      ) : (
        <ul style={css.list}>
          {filtered.map(d => {
            const Glyph = Icon[(d.icon as IconName) ?? "Database"] ?? Icon.Database;
            return (
              <li key={d.id} style={css.card}>
                <div style={css.cardHead}>
                  <span style={css.iconBubble} aria-hidden="true"><Glyph size={18} /></span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ font: "var(--t-title-md)", color: "var(--md-on-surface)" }}>{d.name}</div>
                    <div style={{ font: "var(--t-mono-sm)", color: "var(--md-on-surface-var)" }}>{d.id}</div>
                  </div>
                </div>
                {d.description ? (
                  <p style={{ margin: 0, color: "var(--md-on-surface)", font: "var(--t-body-sm)" }}>{d.description}</p>
                ) : null}
                <div style={css.meta}>
                  <span><strong style={{ fontVariantNumeric: "tabular-nums", color: "var(--md-on-surface)" }}>
                    {(d.recordCount ?? 0).toLocaleString()}
                  </strong> records</span>
                  <span>·</span>
                  <span>v{d.schemaVersion ?? 1}</span>
                  {d.owner ? (<>
                    <span>·</span>
                    <span>{d.owner}</span>
                  </>) : null}
                </div>
                <div style={css.actions}>
                  <Button variant="text" tone="primary" onClick={() => navigate(`/browse/${d.id}`)}>Browse records</Button>
                  <Button variant="tonal" tone="secondary" leadingIcon={<Icon.Edit size={14} />} onClick={() => setEditing(d)}>Edit</Button>
                  <IconButton label={`Delete ${d.name}`} onClick={() => onDelete(d)}><Icon.Trash size={16} /></IconButton>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        title="Create a new domain"
        width={520}
        footer={
          <>
            <Button variant="text" tone="neutral" onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
            <Button variant="filled" tone="primary" onClick={onCreate} disabled={creating || !newName.trim()}>
              {creating ? "Creating…" : "Create domain"}
            </Button>
          </>
        }
      >
        <div style={css.drawerForm}>
          <TextField label="Name *" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Trading Venues" autoFocus />
          <TextField label="Description" value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Short description" />
          <TextField label="Owner team" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} placeholder="e.g. Markets Data" />
          <div>
            <div style={{ font: "var(--t-label-md)", marginBottom: 6 }}>Icon</div>
            <div style={css.iconPicker}>
              {DOMAIN_ICONS.map(name => {
                const Glyph = Icon[name];
                const active = name === newIcon;
                return (
                  <button
                    key={name}
                    type="button"
                    style={{ ...css.iconChoice, ...(active ? css.iconChoiceActive : {}) }}
                    onClick={() => setNewIcon(name)}
                    aria-label={name}
                    aria-pressed={active}
                  >
                    <Glyph size={16} />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </Dialog>

      {/* Edit drawer */}
      <Drawer open={!!editing} onClose={closeEdit} side="right" title={editing ? `Edit ${editing.name}` : ""}>
        {editing ? (
          <div style={css.drawerForm}>
            <TextField label="Name" value={editName} onChange={(e) => setEditName(e.target.value)} />
            <TextField label="Description" value={editDesc} onChange={(e) => setEditDesc(e.target.value)} />
            <TextField label="Owner team" value={editOwner} onChange={(e) => setEditOwner(e.target.value)} />
            <div>
              <div style={{ font: "var(--t-label-md)", marginBottom: 6 }}>Icon</div>
              <div style={css.iconPicker}>
                {DOMAIN_ICONS.map(name => {
                  const Glyph = Icon[name];
                  const active = name === editIcon;
                  return (
                    <button
                      key={name}
                      type="button"
                      style={{ ...css.iconChoice, ...(active ? css.iconChoiceActive : {}) }}
                      onClick={() => setEditIcon(name)}
                      aria-label={name}
                      aria-pressed={active}
                    >
                      <Glyph size={16} />
                    </button>
                  );
                })}
              </div>
            </div>

            <section>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h3 style={{ margin: 0, font: "var(--t-title-md)" }}>Fields ({fields.length})</h3>
                <Button variant="tonal" tone="primary" leadingIcon={<Icon.Plus size={14} />} onClick={addField}>Add field</Button>
              </div>
              {fieldsLoading ? (
                <p style={{ color: "var(--md-on-surface-var)" }}>Loading fields…</p>
              ) : fields.length === 0 ? (
                <p style={{ color: "var(--md-on-surface-var)", font: "var(--t-body-sm)" }}>
                  No fields yet. Records in this domain will only have the system id + name.
                </p>
              ) : (
                <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 10 }}>
                  {fields.map((f, idx) => (
                    <li key={idx} style={css.fieldRow}>
                      <TextField
                        label="Key"
                        value={f.key}
                        onChange={(e) => updateField(idx, { key: e.target.value })}
                        size="sm"
                      />
                      <TextField
                        label="Label"
                        value={f.label}
                        onChange={(e) => updateField(idx, { label: e.target.value })}
                        size="sm"
                      />
                      <label style={{ display: "flex", flexDirection: "column", gap: 4, font: "var(--t-label-md)" }}>
                        <span>Type</span>
                        <select
                          aria-label={`Type for ${f.key}`}
                          value={f.type}
                          onChange={(e) => updateField(idx, { type: e.target.value as FieldType })}
                          style={{ padding: 6, borderRadius: "var(--r-sm)", border: "1px solid var(--md-outline-var)" }}
                        >
                          {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </label>
                      <div style={{ display: "flex", gap: 4 }}>
                        <IconButton label={`Move ${f.key} up`} onClick={() => moveField(idx, -1)} disabled={idx === 0}><Icon.ChevronUp size={14} /></IconButton>
                        <IconButton label={`Move ${f.key} down`} onClick={() => moveField(idx, 1)} disabled={idx === fields.length - 1}><Icon.ChevronDown size={14} /></IconButton>
                        <IconButton label={`Remove ${f.key}`} onClick={() => removeField(idx)}><Icon.Trash size={14} /></IconButton>
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <div style={css.fieldRowDetail}>
                          {f.type === "enum" ? (
                            <TextField label="Enum values (comma-separated)" value={f.enumValues} onChange={(e) => updateField(idx, { enumValues: e.target.value })} size="sm" />
                          ) : <div />}
                          {f.type === "ref" ? (
                            <TextField label="Reference domain id" value={f.refDomain} onChange={(e) => updateField(idx, { refDomain: e.target.value })} size="sm" />
                          ) : <div />}
                        </div>
                        <TextField label="Description" value={f.description} onChange={(e) => updateField(idx, { description: e.target.value })} size="sm" />
                        <div style={css.fieldCheckboxes}>
                          <label><input type="checkbox" checked={f.required} onChange={(e) => updateField(idx, { required: e.target.checked })} /> Required</label>
                          <label><input type="checkbox" checked={f.searchable} onChange={(e) => updateField(idx, { searchable: e.target.checked })} /> Searchable</label>
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </section>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
              <Chip tone="default">v{editing.schemaVersion ?? 1}</Chip>
              <Button variant="text" tone="neutral" onClick={closeEdit} disabled={saving}>Cancel</Button>
              <Button variant="filled" tone="primary" onClick={saveEdits} disabled={saving || !editName.trim()}>
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}

export default Domains;
